import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight, MessageCircle } from "lucide-react";
import { MaskLines } from "../site/Motion";
import { BgVideo } from "../site/BgVideo";
import { ProcessTicker, E2E_VIDEO, E2E_POSTER } from "../site/ProcessTicker";
import { useRfq } from "../site/RfqContext";
import { useSettings, useCategories, waLink } from "../../lib/api";

const TRUST = [
  { k: "Since", v: "2012" },
  { k: "Clients across India", v: "80+" },
  { k: "Product families", v: "08" },
  { k: "Quote model", v: "RFQ" },
];

const Emblem = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [18, -18]), { stiffness: 120, damping: 14 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-22, 22]), { stiffness: 120, damping: 14 });
  const move = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div onMouseMove={move} onMouseLeave={reset} className="relative mx-auto aspect-square w-full max-w-[460px]" style={{ perspective: 1100 }} data-testid="hero-emblem">
      <div className="spin-slow absolute inset-[6%] rounded-full border-[18px] border-slate-300/10" style={{ boxShadow: "inset 0 0 60px rgba(56,189,248,.15), 0 0 80px rgba(10,108,255,.18)" }} />
      <div className="absolute inset-[18%] rounded-full border border-dashed border-sky-400/30" />
      <motion.div
        initial={{ opacity: 0, scale: 0.7, rotateY: -60 }}
        animate={{ opacity: 1, scale: 1, rotateY: 0 }}
        transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1], delay: 0.5 }}
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        className="absolute inset-[16%]"
      >
        <img src="/logo-mark.png" alt="" className="absolute inset-0 h-full w-full object-contain opacity-40 blur-2xl" style={{ transform: "translateZ(-60px)" }} />
        <img src="/logo-mark.png" alt="Autocrat A emblem" className="absolute inset-0 h-full w-full object-contain drop-shadow-[0_30px_40px_rgba(0,0,0,.6)]" style={{ transform: "translateZ(40px)" }} />
      </motion.div>
      <span className="font-mono-x absolute left-0 top-[12%] text-[11px] uppercase tracking-[0.2em] text-slate-400">Shore A 40—90</span>
      <span className="font-mono-x absolute bottom-[10%] right-0 text-[11px] uppercase tracking-[0.2em] text-slate-400">NBR · EPDM · Si · CR</span>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const vid = useRef(null);
  const { open } = useRfq();
  const s = useSettings();
  const { categories } = useCategories();
  const trust = TRUST.map((t) => (t.k === "Product families" && categories.length ? { ...t, v: String(categories.length).padStart(2, "0") } : t));
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const vy = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const ty = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section ref={ref} data-testid="hero-section" className="noise relative overflow-hidden lg:min-h-[100svh] bg-[#07090e] text-white">
      <motion.div style={{ y: vy }} className="absolute inset-0">
        <BgVideo ref={vid} testId="hero-video" className="h-full w-full object-cover opacity-75" src={E2E_VIDEO} poster={E2E_POSTER} />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#07090e]/95 via-[#07090e]/55 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#07090e]/90 via-transparent to-[#07090e]/40" />
      <div className="grid-lines absolute inset-0 opacity-50" />
      <img src="/logo-mark.png" alt="" aria-hidden className="pointer-events-none absolute -right-[18%] top-[14%] w-[78%] max-w-[420px] opacity-[0.16] lg:hidden" />

      <motion.div style={{ y: ty }} className="container-x relative z-10 grid items-center gap-10 pb-14 pt-32 lg:min-h-[100svh] lg:grid-cols-12 lg:pb-40 lg:pt-28">
        <div className="lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="eyebrow flex items-center gap-3 text-sky-400">
            <span className="h-px w-10 bg-sky-400" /> Autocrat Rubber Industries · Est. 2012
          </motion.p>
          <MaskLines
            testId="hero-title"
            className="font-display mt-6 text-[15vw] font-black uppercase leading-[0.86] tracking-tight sm:text-7xl lg:text-[6.2rem]"
            lines={[
              { text: "Driving", className: "text-white" },
              { text: "innovation with", className: "text-steel-grad" },
              { text: "rubber precision.", className: "text-outline" },
            ]}
          />
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }} className="mt-8 max-w-xl text-sm leading-relaxed text-slate-300 md:text-base">
            Molded and extruded rubber components — grommets, bushes, gaskets, seals, O-rings and custom parts — manufactured for OEMs, distributors and traders across India.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8 }} className="mt-10 flex flex-wrap gap-4">
            <button data-testid="hero-rfq-button" onClick={() => open()} className="btn-electric">Request a Quote <ArrowRight className="h-4 w-4" /></button>
            <a data-testid="hero-whatsapp-button" href={waLink(s.whatsapp)} target="_blank" rel="noreferrer" className="btn-ghost-steel"><MessageCircle className="h-4 w-4 text-[#25D366]" /> WhatsApp</a>
          </motion.div>
        </div>
        <div className="lg:col-span-5 lg:block">
          <div className="hidden lg:block"><Emblem /></div>
          <ProcessTicker videoRef={vid} className="max-w-sm lg:ml-auto lg:mt-4" />
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.25, duration: 0.9 }} data-testid="trust-bar" className="relative z-20 border-t lg:absolute lg:inset-x-0 lg:bottom-0 border-slate-800/80 bg-[#07090e]/70 backdrop-blur-xl">
        <div className="container-x grid grid-cols-2 md:grid-cols-4">
          {trust.map((t, i) => (
            <div key={t.k} className={`py-5 md:py-7 ${i % 2 ? "pl-5 md:pl-8" : "md:pl-8 first:pl-0"} ${i ? "md:border-l border-slate-800" : ""}`}>
              <p className="font-display text-3xl font-extrabold text-white md:text-5xl">{t.v}</p>
              <p className="eyebrow mt-1 !text-[10px] text-slate-400">{t.k}</p>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};
